import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/pages/ForgotPassword";
import { PasswordFields, passwordProblem } from "@/components/app/PasswordFields";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, email: "", error: "" });
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/${token}`)
      .then(({ data }) => setState({ loading: false, email: data.email, error: "" }))
      .catch((e) => setState({ loading: false, email: "", error: fmtErr(e.response?.data?.detail) }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const p = passwordProblem(pw.next, pw.confirm);
    if (p) { toast.error(p); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw.next });
      localStorage.removeItem("tp_token");
      toast.success("Password updated. Please sign in with your new password.");
      nav("/login");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell>
      <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        {state.loading ? <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /> : state.error ? (
          <div className="text-center" data-testid="reset-invalid">
            <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold mb-2">Link not valid</h1>
            <p className="text-sm text-slate-500">{state.error}</p>
            <Link to="/forgot-password" className="inline-block mt-5 text-sm text-blue-600 font-medium" data-testid="reset-request-new">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form">
            <h1 className="text-2xl font-bold mb-1">Choose a new password</h1>
            <p className="text-sm text-slate-500 mb-6">For <span className="font-medium text-slate-700" data-testid="reset-email">{state.email}</span>. You'll be signed out on all other devices.</p>
            <PasswordFields value={pw} onChange={setPw} prefix="reset" />
            <button data-testid="reset-submit" disabled={busy} className="mt-6 w-full py-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm disabled:opacity-60">
              {busy ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </AuthShell>
  );
}
